import React, { useEffect, useState } from 'react';
import { withRouter } from 'react-router-dom';
import ApprovalRewards from './../../presentation/approvalRewards/ApprovalRewards.jsx';
import ApprovedContainer from './../ApprovedContainer/ApprovedContainer.jsx';
import PendingApprovalContainer from './../pendingApprovalContainer/PendingApprovalContainer.jsx';
import { message, Modal, Button } from 'antd';
import { withApollo } from 'react-apollo';
import gql from 'graphql-tag';

import { useSelector } from 'react-redux';

const GET_APPROVAL_COUNTS = gql`
  query GetApprovalCounts($companyId: ID!) {
    getApprovalCounts(companyId: $companyId) {
      pending
      approved
      declined
      total
    }
  }
`;

const UPDATE_BADGE_ASSIGNMENT = gql`
	mutation UpdateBadgeAssignment($input: UpdateBadgeAssignmentInput!) {
		updateBadgeAssignment(input: $input) {
			id
			approvalStatus
			sentWithoutSnappyGrant
			completedAt
		}
	}
`;

const UPDATE_USER = gql`
	mutation UpdateUser($input: UpdateUserInput!) {
		updateUser(input: $input) {
			id
			recognitionEvents {
				currentBudget
				amountPending
				additionalBudgetTotal
				lastUpdated
				eventList {
					adminOverride
					approvalResult
					budgetAfterTotal
					budgetBeforeTotal
					completedById
					completedDate
					createdDate
					pendingApproval
					recognitionAmount
					recognitionId
					recognitionName
					sentById
					sentByManager
					sentReason
					snappyCampaignTierId
				}
			}
		}
	}
`;

// Mutation to create snappy gift for recognition mode
const CREATE_SNAPPY_GIFT = gql`
	mutation CreateSnappyGift($input: CreateSnappyGiftInput!) {
		createSnappyGift(input: $input) {
			id
			giftId
			campaignTierId
			snappyGrantId
			status
			giftDetails {
				link
				status
			}
			createdAt
		}
	}
`;

const ApprovalRewardsContainer = (props) => {
	const currentUser = useSelector((state) => state.user.currentUser);
	const companyId = currentUser?.company?.id;

	const [pendingRecordId, setPendingRecordId] = useState()
	const [pendingRecord, setPendingRecord] = useState(null)
	const [isApprove, setIsApprove] = useState(false);
	const [isDecline, setIsDecline] = useState(false);
	const [isBudgetModal, setIsBudgetModal] = useState(false);
	const [approvalCountData, setApprovalCountData] = useState({
		pending: 0,
		approved: 0,
		declined: 0,
		total: 0
	})
	const [isTableRefresh, setTableRefresh] = useState(true)

	useEffect(() => {
		getApprovalRewardCountData()
	}, [])

	const getApprovalRewardCountData = async () => {
		if (!companyId) {
			message.error('Company ID not found');
			return;
		}

		try {
			const { data } = await props.client.query({
				query: GET_APPROVAL_COUNTS,
				variables: { companyId },
				fetchPolicy: 'network-only'
			});

			if (data?.getApprovalCounts) {
				setApprovalCountData(data.getApprovalCounts);
			} else {
				message.log('Failed to fetch approval counts');
			}
		} catch (error) {
			console.log("Error fetching approval counts:", error);
		}
	};

	const handleApproveAndDeclined = async (recordId, status, record = null, declineReason = null) => {
		if (!recordId) {
			message.error('Record ID not found');
			return;
		}

		if (status === 'Declined') {
			try {
				const { data } = await props.client.mutate({
					mutation: UPDATE_BADGE_ASSIGNMENT,
					variables: {
						input: {
							id: recordId,
							companyId: companyId,
							approvalStatus: 'Declined',
							completedAt: new Date().toISOString(),
							declineReason: declineReason
						}
					}
				});

				if (data?.updateBadgeAssignment) {
					setIsDecline(false);
					setIsApprove(false);
					setTableRefresh(true);
					getApprovalRewardCountData();
					message.success('Recognition declined successfully');
				} else {
					message.error('Failed to decline recognition');
				}
			} catch (error) {
				console.error('Error declining recognition:', error);
				message.error('Failed to decline recognition');
			}
			return;
		}

		// For approval, check budget first
		if (status === 'Approved') {
			const currentRecord = record || pendingRecord;
			if (!currentRecord) {
				message.error('Record data not found');
				return;
			}

			const remainingBudget = currentRecord.remainingBudget || 0;
			const totalCost = currentRecord.costInCurrency || 0;

			if (remainingBudget < totalCost && totalCost > 0) {
				setIsBudgetModal(true);
				return;
			}

			await approveWithGift(recordId, currentRecord);
		}
	};

	const approveWithGift = async (recordId, record, sentWithoutSnappyGrant = false, overrideBudget = false) => {
		try {
			const updateInput = {
				id: recordId,
				companyId: companyId,
				approvalStatus: 'Approved',
				completedAt: new Date().toISOString(),
				sentWithoutSnappyGrant: sentWithoutSnappyGrant
			};

			const { data } = await props.client.mutate({
				mutation: UPDATE_BADGE_ASSIGNMENT,
				variables: { input: updateInput }
			});

			if (data?.updateBadgeAssignment) {
				if (overrideBudget && record.costInCurrency > 0) {
					await updateUserRecognitionEvents(record, true);
				}

				await handlePostApprovalActions(record, sentWithoutSnappyGrant);

				setIsDecline(false);
				setIsApprove(false);
				setIsBudgetModal(false);
				setTableRefresh(true);
				getApprovalRewardCountData();
				message.success('Recognition approved successfully');
			} else {
				message.error('Failed to approve recognition');
			}
		} catch (error) {
			console.error('Error approving recognition:', error);
			message.error('Failed to approve recognition');
		}
	};

	const handlePostApprovalActions = async (record, sentWithoutSnappyGrant) => {
		try {
			const recipient = record.rawData?.user;
			const company = recipient?.company;
			
			const isRecognitionMode = company?.recognitionSettings?.rewardsEarnedMechanism === 'recognition';

			if (isRecognitionMode && !sentWithoutSnappyGrant && record.snappyGrantId) {
				await createSnappyGiftForRecipient(record);
			}

			await createNotificationForRecipient(record, sentWithoutSnappyGrant);

		} catch (error) {
			console.error('Error in post approval actions:', error);
		}
	};

	const createSnappyGiftForRecipient = async (record) => {
		try {
			const snappyGiftInput = {
				userId: record.recipientId,
				campaignTierId: record.rawData?.badge?.snappyGrant?.campaignTierId,
				snappyGrantId: record.snappyGrantId,
				badgeAssignmentId: record.id,
				status: 'pending'
			};

			const { data } = await props.client.mutate({
				mutation: CREATE_SNAPPY_GIFT,
				variables: { input: snappyGiftInput }
			});

			if (data?.createSnappyGift) {
				console.log('Snappy gift created for recipient:', data.createSnappyGift);
			}
		} catch (error) {
			console.error('Error creating snappy gift:', error);
		}
	};

	const createNotificationForRecipient = async (record, sentWithoutSnappyGrant) => {
		try {
			const notificationInput = {
				userId: record.recipientId,
				type: 'badge_assignment',
				title: `New Recognition: ${record.badgeName}`,
				message: sentWithoutSnappyGrant
					? 'You received a recognition badge!'
					: 'You received a recognition with a gift attached!',
				data: {
					badgeAssignmentId: record.id,
					badgeName: record.badgeName,
					sentWithoutSnappyGrant: sentWithoutSnappyGrant,
					snappyGrantId: record.snappyGrantId
				},
				isRead: false
			};

			const { data } = await props.client.mutate({
				mutation: CREATE_NOTIFICATION,
				variables: { input: notificationInput }
			});

			if (data?.createNotification) {
				console.log('Notification created for recipient:', data.createNotification);
			}
		} catch (error) {
			console.error('Error creating notification:', error);
		}
	};

	const updateUserRecognitionEvents = async (record, adminOverride = false) => {
		try {
			const userId = record.recipientId;
			const costInCurrency = record.costInCurrency || 0;
			
			const newTransaction = {
				adminOverride: adminOverride,
				approvalResult: true,
				budgetAfterTotal: record.remainingBudget + (adminOverride ? costInCurrency : 0),
				budgetBeforeTotal: record.remainingBudget,
				completedById: currentUser.id,
				completedDate: new Date().toISOString(),
				createdDate: new Date().toISOString(),
				pendingApproval: false,
				recognitionAmount: costInCurrency,
				recognitionId: record.id,
				recognitionName: record.badgeName,
				sentById: record.createdBy,
				sentByManager: false,
				sentReason: 'Badge Assignment Approval',
				snappyCampaignTierId: record.rawData?.badge?.snappyGrant?.id || null
			};

			const currentRecognitionEvents = record.rawData?.user?.recognitionEvents || {
				currentBudget: 0,
				amountPending: 0,
				additionalBudgetTotal: 0,
				eventList: []
			};

			const updatedRecognitionEvents = {
				...currentRecognitionEvents,
				additionalBudgetTotal: (currentRecognitionEvents.additionalBudgetTotal || 0) + (adminOverride ? costInCurrency : 0),
				lastUpdated: new Date().toISOString(),
				eventList: [...(currentRecognitionEvents.eventList || []), newTransaction]
			};

			await props.client.mutate({
				mutation: UPDATE_USER,
				variables: {
					input: {
						id: userId,
						recognitionEvents: updatedRecognitionEvents
					}
				}
			});
		} catch (error) {
			console.log('Error updating user recognition events:', error);
		}
	};

	const handleApprovalCLick = (record) => {
		setPendingRecordId(record?.id);
		setPendingRecord(record);
		setIsApprove(true);
	};

	const handleDeclineClick = (record) => {
		setPendingRecordId(record?.id);
		setPendingRecord(record);
		setIsDecline(true);
	};

	return (
		<div>
			<ApprovalRewards approvalCountData={approvalCountData} />
			<PendingApprovalContainer
				isPendingReward={true}
				pendingRecordId={pendingRecordId}
				setIsDecline={setIsDecline}
				setIsApprove={setIsApprove}
				isApprove={isApprove}
				isDecline={isDecline}
				handleApprovalCLick={handleApprovalCLick}
				handleDeclineClick={handleDeclineClick}
				handleApproveAndDeclined={handleApproveAndDeclined}
				setPendingRecordId={setPendingRecordId}
				setTableRefresh={setTableRefresh}
				isTableRefresh={isTableRefresh}
				pendingRecord={pendingRecord} />
			<ApprovedContainer
				isPendingReward={false}
				pendingRecordId={pendingRecordId}
				setPendingRecordId={setPendingRecordId}
				isTableRefresh={isTableRefresh}
				setTableRefresh={setTableRefresh}
			/>
			
			{/* Budget Override Modal */}
			<Modal
				destroyOnClose
				width={500}
				open={isBudgetModal}
				title="Budget Override Required"
				footer={null}
				className="confirmation-modal"
				onCancel={() => {
					setIsBudgetModal(false);
				}}
			>
				<div>
					<p className="text-center modal-detail" style={{ marginBottom: '20px' }}>
						Snappy Grant would be more than the employee's total budget, do you want to send this without a gift attached?
					</p>
					<div className="text-center" style={{ display: 'flex', justifyContent: 'center', gap: '10px' }}>
						<Button
							className="approve-btn"
							type="primary"
							onClick={() => approveWithGift(pendingRecordId, pendingRecord, true, false)}
							style={{ marginRight: '10px' }}
						>
							Yes
						</Button>
						<Button
							className="approve-btn"
							danger={true}
							onClick={() => props.history.push('/employee-manager')}
							style={{ marginRight: '10px' }}
						>
							No, Override the budget
						</Button>
						<Button
							className="decline-btn"
							ghost={true}
							onClick={() => setIsBudgetModal(false)}
						>
							No
						</Button>
					</div>
				</div>
			</Modal>
		</div>
	);
};

export default withRouter(withApollo(ApprovalRewardsContainer));
